import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { HeroBlockA as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "../../styles/composable/product-list-hero.module.scss";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows ``HeroBlockA`` (homepage / product-list hero,
 * "default" style). DOM mirrors the legacy Django template
 * ``cms/components/homepage_hero_a.html`` exactly.
 *
 * The block is rendered regardless of ``hero_status``; the
 * ``display-status--{status}`` class on the outer wrapper drives the
 * existing CSS hide behaviour for the "hidden" status. The hero image is
 * applied as an inline ``background-image`` only when an image exists (to
 * match the Django ``{% if %}``). ``hero_button_text``/``hero_headline``/
 * ``hero_copy`` are RichText; the prices are integers rendered as plain
 * numbers with the "Starting at" + strike (optional) + actual markup.
 */
export const HeroBlockA = (props: Props): React.JSX.Element => {
    const v = props.value;
    return (
        <div className={`l-full-width display-status--${v.hero_status ?? ""}`}>
            <div
                className={concatClassNames([
                    "hero",
                    // Homepage CMS raw-HTML <style> targets this literal.
                    "product-list-hero",
                    styles.productListHero,
                    "pillows-hero",
                    "l-full-width",
                ])}
                style={
                    v.hero_image
                        ? { backgroundImage: `url(${v.hero_image.url})` }
                        : undefined
                }
            >
                <div
                    className={`pillows-hero__content pillows-hero__content--${v.hero_alignment ?? ""} l-capped-width`}
                >
                    <div
                        className={concatClassNames([
                            styles.productListHeroCopy,
                            "pillows-hero__copy",
                        ])}
                    >
                        {v.hero_button_text ? (
                            <div
                                className={concatClassNames([
                                    styles.productListHeroCategory,
                                    "pillows-hero__category",
                                ])}
                            >
                                <RichTextBlock value={v.hero_button_text} />
                            </div>
                        ) : null}
                        <div
                            className={concatClassNames([
                                styles.productListHeroTitle,
                                "pillows-hero__title",
                            ])}
                        >
                            <RichTextBlock value={v.hero_headline ?? ""} />
                        </div>
                        <div
                            className={concatClassNames([
                                styles.productListHeroDescription,
                                "pillows-hero__description",
                            ])}
                        >
                            <RichTextBlock value={v.hero_copy ?? ""} />
                        </div>
                        <div className="pillows-hero__price">
                            {t`Starting at `}
                            {v.hero_starting_price ? (
                                <span className="pillows-hero__price--strike">
                                    <span className="ada-screenreader-only">
                                        {t`Original price: `}
                                    </span>
                                    ${v.hero_starting_price}
                                </span>
                            ) : null}
                            <br />
                            <span className="pillows-hero__price__actual">
                                <span className="ada-screenreader-only">
                                    {t`Current price: `}
                                </span>
                                <span className="pillows-hero__price__actual--sup">
                                    <sup>$</sup>
                                </span>
                                <span className="pillows-hero__price__actual-price">
                                    {v.hero_retail_price}
                                </span>
                            </span>
                        </div>
                        <a href={v.hero_link?.url ?? ""}>
                            <div className="button pillows-hero__button">
                                {v.hero_link_name ?? ""}
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};
